// client/transports/websocketTransport.ts
import { A } from "@adornis/base/env-info.js";
import { ignoreSymbol, setClientChildLogger } from "@adornis/base/logging.js";
import Cookies from "js-cookie";
import { BehaviorSubject, Observable, Subject, lastValueFrom } from "rxjs";
import { debounceTime, filter, share, startWith, switchMap, take } from "rxjs/operators";
import SockjsClient from "sockjs-client/dist/sockjs.js";
import {
  MessageTypes,
  TerminateReason
} from "../../protocol.js";
import { combineErrors } from "../../utils/helpers.js";
import { BaseQLClient } from "../baseqlClient.js";
var BaseQLWebsocketTransport = class _BaseQLWebsocketTransport {
  constructor(options) {
    this.options = options;
    /** if we have a session this is the sessionID used to identify it */
    this.sessionID = localStorage.getItem("sessionID") ?? "";
    this.connectionID = "";
    this.destroyed = false;
    /** do we have a session? */
    this._session = new BehaviorSubject(void 0);
    /** counter to provide a unique operation id */
    this._operationCounter = 0;
    /**
     * map to hold all currently active subscriptions
     * @see subMapValue
     */
    this._subscriptionObservables = /* @__PURE__ */ new Map();
    this._online = navigator.onLine;
    this._connectionTries = 0;
    this._keepAlivePing = new Subject();
    /** Run a mutation against the server */
    this.mutation = this.query;
    console.debug("Constructing websocketTransport");
    this._connect();
    this._onlineDetectionSub = A.online().subscribe((on) => {
      this._online = on;
      if (this._client && on === false) {
        this._client.close();
      }
      if (!this._client && on) {
        this._reconnect();
      }
    });
  }
  static {
    this.instance = void 0;
  }
  /** retrieve singleton instance. Creates if none present. */
  static get(options) {
    this.instance ??= new _BaseQLWebsocketTransport(options);
    return this.instance;
  }
  get available() {
    return this._session;
  }
  get _serverWillRestart() {
    return this._terminateReason && [TerminateReason.RESTART, TerminateReason.SCHEMA_REBUILD].includes(this._terminateReason);
  }
  _connect(reconnect = false) {
    this._client = new SockjsClient(this.options?.endpoint ?? A.absoluteUrl("/api/graphql-sockjs"), null, {
      timeout: 2e3,
      transports: [`websocket`, "eventsource", "xhr-polling", "jsonp-polling"]
    });
    this._keepAlivePing.pipe(
      startWith(null),
      debounceTime(15e3),
      filter(() => BaseQLClient.isConnectedSync())
    ).subscribe(() => this._client?.close());
    this._client.onopen = async () => {
      this._connectionTries = 0;
      const initPayload = await this.options?.getInitPayload?.(this.sessionID) ?? { sessionID: this.sessionID };
      this.startSession(initPayload, reconnect);
      await lastValueFrom(this._waitForSession());
      await this.options?.onConnect?.(this._terminateReason);
      if (reconnect)
        this.restartOpenOperations();
      this._terminateReason = void 0;
      this._keepAlivePing.next();
    };
    this._client.onmessage = (e) => {
      try {
        this._keepAlivePing.next();
        const message = JSON.parse(e.data);
        let sub;
        switch (message.type) {
          case MessageTypes.GQL_DATA:
            sub = this._subscriptionObservables.get(message.id);
            if (!sub)
              return console.warn("No subject stored for Operation ID " + message.id);
            console.debug("BaseQL websocket: data", message.id, sub.queryName);
            sub.sub.next(message.payload);
            return;
          case MessageTypes.GQL_ERROR:
            sub = this._subscriptionObservables.get(message.id);
            if (!sub)
              return console.warn("No subject stored for Operation ID " + message.id);
            sub.serverCompleted = true;
            sub.sub.error(message.payload.errors);
            this._subscriptionObservables.delete(message.id);
            return;
          case MessageTypes.GQL_COMPLETE:
            sub = this._subscriptionObservables.get(message.id);
            if (!sub)
              return;
            console.debug("BaseQL websocket: complete", message.id, sub.queryName);
            sub.serverCompleted = true;
            sub.sub.complete();
            this._subscriptionObservables.delete(message.id);
            return;
          case MessageTypes.GQL_CONNECTION_ACK:
            this.sessionID = message.payload.sessionID;
            BaseQLClient.sessionID = message.payload.sessionID;
            BaseQLClient.connectionID = message.payload.connectionID;
            Cookies.set("baseQLSession", message.payload.sessionID);
            setClientChildLogger({ sessionID: message.payload.sessionID });
            localStorage.setItem("sessionID", message.payload.sessionID);
            this._session.next(true);
            console.debug("BaseQL websocket: Session opened", message.payload.sessionID);
            return;
          case MessageTypes.GQL_CONNECTION_ACK_CLOSE:
            console.debug("BaseQL websocket: Session closed");
            return;
          case MessageTypes.GQL_CONNECTION_ERROR: {
            console.debug("BaseQL websocket: Connection ERR");
            const combinedError = combineErrors(message.payload.errors);
            console.error(
              `%c\u{1F32A}\uFE0F error from the server on session creation`,
              "color: white; font-size: 1.6em; font-weight: bold",
              `
${combinedError}`
            );
            combinedError[ignoreSymbol] = true;
            throw combinedError;
          }
          case MessageTypes.GQL_CONNECTION_TERMINATE:
            this._terminateReason = message.reason;
            return;
          case MessageTypes.GQL_CONNECTION_KEEP_ALIVE:
            return;
          default:
            return;
        }
      } catch (error) {
        console.warn({ message: e.data, error }, "Got malformed message on websocket, ignoring");
      }
    };
    this._client.onerror = (e) => {
      console.log(e);
      this._reconnect();
    };
    this._client.onclose = (e) => {
      console.log(e);
      if (e.wasClean && this._serverWillRestart) {
        console.debug("BaseQL websocket: Server closed the connection but indicated that its restarting...");
        this._connectionTries = 3;
      }
      this._reconnect();
    };
  }
  _reconnect() {
    this._session.next(false);
    delete this._client;
    if (!this._online)
      return;
    this._connectionTries++;
    clearTimeout(this._reconnectTimeout);
    this._reconnectTimeout = setTimeout(() => {
      this._connect(true);
    }, 1e3 + Math.pow(2, this._connectionTries) * 50);
  }
  /**
   * @returns Observable which fires once when a session is active.
   */
  _waitForSession() {
    return this._session.pipe(
      filter((v) => !!v),
      take(1)
    );
  }
  /**
   * Business logic for any GQL operation. Separated into a private function to not expose Subscriber.
   */
  _subscribe(op, payload) {
    const id = ++this._operationCounter + "";
    const obs = new Observable((subscriber) => {
      this._subscriptionObservables.set(id, {
        sub: subscriber,
        serverCompleted: false,
        queryName: op.name,
        payload,
        restartOnCleanClose: !op.options.requestHandlerMeta?.transport?.dontRestart
      });
      return payload.subscription ? () => this._unsubscribe(id) : void 0;
    });
    console.debug("BaseQL websocket: sending op", id, op.name);
    this._client?.send(JSON.stringify({ id, type: MessageTypes.GQL_START, payload }));
    return obs;
  }
  /**
   * Stop currently running subscription
   * @param id id of subscription to stop
   */
  _unsubscribe(id) {
    const sub = this._subscriptionObservables.get(id);
    if (!sub)
      return;
    if (!sub.serverCompleted)
      this._client?.send(JSON.stringify({ id, type: MessageTypes.GQL_STOP }));
    sub.sub.complete();
    this._subscriptionObservables.delete(id);
  }
  restartOpenOperations() {
    for (const [id, operation] of this._subscriptionObservables) {
      if (!operation.restartOnCleanClose && this._serverWillRestart)
        continue;
      console.debug("restarting op", id);
      this._client?.send(
        JSON.stringify({ id, type: MessageTypes.GQL_START, payload: operation.payload, reconnect: true })
      );
    }
  }
  /** Start a subscription */
  subscription(op, payload) {
    return this._waitForSession().pipe(
      switchMap(() => this._subscribe(op, payload)),
      share()
    );
  }
  /** Run a query against the server */
  async query(op, payload) {
    if (payload.subscription)
      throw new Error("query/mutation cannot specify a subscription!");
    if (!payload.query && !payload.mutation)
      throw new Error("operation has to specify a query or a mutation!");
    await lastValueFrom(this._waitForSession());
    const obs = this._subscribe(op, payload);
    return lastValueFrom(obs);
  }
  stop() {
  }
  destroy() {
    if (this.destroyed)
      throw new Error("Destroying an already destroyed BaseQLWebsocketTransport");
    this.cleanup();
    this._onlineDetectionSub.unsubscribe();
    this.destroyed = true;
  }
  closeSession() {
    this._session.next(false);
    this.cleanup();
    this._client?.send(JSON.stringify({ type: MessageTypes.GQL_CONNECTION_CLOSE }));
  }
  /**
   * Clean up all observables
   */
  cleanup() {
    this._client?.close();
    this._subscriptionObservables.forEach((sub, id) => this._unsubscribe(id));
    this._subscriptionObservables.clear();
  }
  startSession(payload, reconnect = false) {
    if (this._session.getValue() && !reconnect)
      this.closeSession();
    this._client?.send(JSON.stringify({ type: MessageTypes.GQL_CONNECTION_INIT, payload }));
  }
  /** Close the session if one exists and terminate the websocket connection afterwards. */
  terminate() {
    this._session.next(false);
    this.closeSession();
    this._client?.send(JSON.stringify({ type: MessageTypes.GQL_CONNECTION_TERMINATE }));
  }
};
export {
  BaseQLWebsocketTransport
};
